/**
 * useMaintenanceData Hook
 * 점검 관리 데이터 훅 (CRUD)
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { maintenanceApi } from '../api/maintenanceApi';
import {
    CreateMaintenanceRequestDto,
    Maintenance,
    UpdateMaintenanceRequestDto,
} from '../types/maintenance.types';
import { maintenanceListFromDto } from '../utils/maintenanceUtils';
import { commonCodeApi } from "@/shared/api/commonCodeApi";
import { CommonCodeDto } from "@/shared/types/commonCode.types";
import { ToastType } from "@/shared/components/feedback/Toast/Toast.types";
import { useToast } from "@/shared/components/feedback/Toast/ToastContext";

export const useMaintenanceData = () => {
    const { showToast } = useToast();
    const [maintenances, setMaintenances] = useState<Maintenance[]>([]);
    const [loading, setLoading] = useState(false);
    const [msgCodes, setMsgCodes] = useState<CommonCodeDto[]>([]);
    const hasFetchedMsgCodes = useRef(false);

    const fetchMaintenances = useCallback(async () => {
        try {
            setLoading(true);
            const response = await maintenanceApi.getMaintenanceList();
            const list = maintenanceListFromDto(response);
            setMaintenances(list);
        } catch {
            showToast('점검 목록을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
            setMaintenances([]);
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    const createMaintenance = useCallback(async (request: CreateMaintenanceRequestDto): Promise<boolean> => {
        try {
            setLoading(true);
            const response = await maintenanceApi.createMaintenance(request);
            if (response.success) {
                showToast('점검이 등록되었습니다.', { type: ToastType.SUCCESS });
                await fetchMaintenances();
                return true;
            } else {
                showToast(response.message || '점검 등록에 실패했습니다.', { type: ToastType.ERROR });
                return false;
            }
        } catch {
            showToast('점검 등록에 실패했습니다.', { type: ToastType.ERROR });
            return false;
        } finally {
            setLoading(false);
        }
    }, [showToast, fetchMaintenances]);

    const updateMaintenance = useCallback(async (request: UpdateMaintenanceRequestDto): Promise<boolean> => {
        try {
            setLoading(true);
            const response = await maintenanceApi.updateMaintenance(request);
            if (response.success) {
                showToast('점검이 수정되었습니다.', { type: ToastType.SUCCESS });
                await fetchMaintenances();
                return true;
            } else {
                showToast(response.message || '점검 수정에 실패했습니다.', { type: ToastType.ERROR });
                return false;
            }
        } catch {
            showToast('점검 수정에 실패했습니다.', { type: ToastType.ERROR });
            return false;
        } finally {
            setLoading(false);
        }
    }, [showToast, fetchMaintenances]);

    const deleteMaintenance = useCallback(async (seqNo: number): Promise<boolean> => {
        try {
            setLoading(true);
            const response = await maintenanceApi.deleteMaintenance(seqNo);
            if (response.success) {
                showToast('점검이 삭제되었습니다.', { type: ToastType.SUCCESS });
                await fetchMaintenances();
                return true;
            } else {
                showToast(response.message || '점검 삭제에 실패했습니다.', { type: ToastType.ERROR });
                return false;
            }
        } catch {
            showToast('점검 삭제에 실패했습니다.', { type: ToastType.ERROR });
            return false;
        } finally {
            setLoading(false);
        }
    }, [showToast, fetchMaintenances]);

    // 점검 문구 코드 목록 조회
    useEffect(() => {
        if (hasFetchedMsgCodes.current) return;

        let isCancelled = false;

        const fetchMsgCodes = async () => {
            try {
                const response = await commonCodeApi.getCommonCodeList('MAINT_MSG');
                if (!isCancelled) {
                    setMsgCodes(response.codes || []);
                    hasFetchedMsgCodes.current = true;
                }
            } catch {
                // 에러는 무시 (취소된 경우 포함)
            }
        };

        fetchMsgCodes();
        return () => { isCancelled = true; };
    }, []);

    // 초기 데이터 로드
    useEffect(() => {
        fetchMaintenances();
    }, [fetchMaintenances]);

    return {
        maintenances,
        loading,
        msgCodes,
        fetchMaintenances,
        createMaintenance,
        updateMaintenance,
        deleteMaintenance,
    };
};
